import { apiDELETE, apiGET, apiPATCH, apiPOST } from 'utils/apiClient'
import type {
  AddParticipantsPayload,
  AddParticipantsResponse,
  ChallengeCategory,
  ChallengeCreateAndUpdateData,
  ChallengeImageType,
  CreateChallengeResponse,
  DeleteChallengeResponse,
  DeleteImageResponse,
  ConfirmImageUploadResponse,
  GetChallengeResponse,
  GetChallengeTypesResponse,
  GetImageResponse,
  GetLeaderboardResponse,
  GetUserChallengeRoundsResponse,
  ListChallengesResponse,
  ListUserChallengesResponse,
  OverrideRoundEligibilityPayload,
  OverrideRoundEligibilityResponse,
  RecalculateChallengeResponse,
  RemoveParticipantResponse,
  RequestImageUploadResponse,
  TransitionChallengeStatePayload,
  TransitionChallengeStateResponse,
  TransitionParticipantStateResponse,
  UpdateChallengePayload,
  UpdateChallengeResponse,
  GetChallengeInsightsResponse,
} from './types'
import { ChallengeComputedState } from 'utils/challenges'
import { CloneChallengeResponse } from './generated/core-gateway'

/**
 * The /api/2/* endpoints via legacy axios helpers.
 */

/** CoreV2: GET /challenges-admin/v1/challenges/{challengeId} */
export const getChallenge = async (challengeId: string): Promise<GetChallengeResponse> => {
  const response = await apiGET({ url: `/api/2/challenges/${challengeId}` })
  return response.data as GetChallengeResponse
}

/** CoreV2: GET /challenges-admin/v1/challenges/{challengeId}/insights */
export const getChallengeInsights = async (challengeId: string): Promise<GetChallengeInsightsResponse> => {
  const response = await apiGET({ url: `/api/2/challenges/${challengeId}/insights` })
  return response.data as GetChallengeInsightsResponse
}

/** CoreV2: DELETE /challenges-admin/v1/challenges/{challengeId} */
export const deleteChallenge = async (challengeId: string): Promise<DeleteChallengeResponse> => {
  const response = await apiDELETE({ url: `/api/2/challenges/${challengeId}` })
  return response.data as DeleteChallengeResponse
}

/** CoreV2: GET /challenges-admin/v1/challenge-types */
export const getChallengeTypes = async (): Promise<GetChallengeTypesResponse> => {
  const response = await apiGET({ url: '/api/2/challenge-types' })
  return response.data as GetChallengeTypesResponse
}

/** CoreV2: GET /challenges-participant/v1/challenges/{challengeId}/leaderboard */
export const getLeaderboard = async (challengeId: string): Promise<GetLeaderboardResponse> => {
  const response = await apiGET({ url: `/api/2/challenges/${challengeId}/leaderboard` })
  return response.data as GetLeaderboardResponse
}

/** CoreV2: GET /challenges-participant/v1/challenges/{challengeId}/clone */
export const cloneChallenge = async (challengeId: string): Promise<CloneChallengeResponse> => {
  const response = await apiPOST({ url: `/api/2/challenges/${challengeId}/clone` })
  return response.data as CloneChallengeResponse
}

/** CoreV2: GET /challenges-media/v1/challenges/{challengeId}/image/{challengeImageType} */
export const getChallengeImage = async (challengeId: string, challengeImageType: string): Promise<GetImageResponse> => {
  const response = await apiGET({ url: `/api/2/challenges/${challengeId}/image/${challengeImageType}` })
  return response.data as GetImageResponse
}

/** CoreV2: GET /challenges-participant/v1/users/{userId}/challenges */
export const getUserChallenges = async (
  userId: string,
  query?: { category?: ChallengeCategory; cursor?: string },
): Promise<ListUserChallengesResponse> => {
  const response = await apiGET({
    url: `/api/2/users/${userId}/challenges`,
    params: {
      ...(query?.category && { category: query.category }),
      ...(query?.cursor && { cursor: query.cursor }),
    },
  })
  return response.data as ListUserChallengesResponse
}

/** CoreV2: POST /challenges-participant/v1/challenges/{challengeId}/participants */
export const addParticipants = async (
  challengeId: string,
  body: AddParticipantsPayload,
): Promise<AddParticipantsResponse> => {
  const response = await apiPOST({ url: `/api/2/challenges/${challengeId}/participants`, data: body })
  return response.data as AddParticipantsResponse
}

/** CoreV2: DELETE /challenges-participant/v1/challenges/{challengeId}/participants/{participantId} */
export const removeParticipant = async (
  challengeId: string,
  participantId: string,
): Promise<RemoveParticipantResponse> => {
  const response = await apiDELETE({ url: `/api/2/challenges/${challengeId}/participants/${participantId}` })
  return response.data as RemoveParticipantResponse
}

/** CoreV2: POST /challenges-participant/v1/challenges/{challengeId}/participants/{participantId}/actions/{action} */
export const transitionParticipant = async (
  challengeId: string,
  participantId: string,
  action: string,
): Promise<TransitionParticipantStateResponse> => {
  const response = await apiPOST({
    url: `/api/2/challenges/${challengeId}/participants/${participantId}/actions/${action}`,
  })
  return response.data as TransitionParticipantStateResponse
}

/** CoreV2: GET /challenges-admin/v1/challenges/{challengeId}/participants/{participantId}/rounds */
export const getParticipantRoundsForChallenge = async (
  challengeId: string,
  participantId: string,
): Promise<GetUserChallengeRoundsResponse> => {
  const response = await apiGET({ url: `/api/2/challenges/${challengeId}/participants/${participantId}/rounds` })
  return response.data as GetUserChallengeRoundsResponse
}

/** CoreV2: GET /challenges-admin/v1/challenges (list all) */
export const listChallenges = async (query?: {
  category?: ChallengeCategory[]
  challengeComputedState?: ChallengeComputedState[]
}): Promise<ListChallengesResponse> => {
  const params: Record<string, string | string[]> = {}

  if (query?.category) {
    params.category = query.category.join(',')
  }

  if (query?.challengeComputedState?.length) {
    params.computedState = query.challengeComputedState
  }

  const response = await apiGET({
    url: '/api/2/challenges',
    params: Object.keys(params).length ? params : undefined,
  })

  return response.data as ListChallengesResponse
}

/** CoreV2: POST /challenges-admin/v1/challenges */
export const createChallenge = async (body: ChallengeCreateAndUpdateData): Promise<CreateChallengeResponse> => {
  const response = await apiPOST({
    url: '/api/2/challenges',
    data: body,
  })

  return response.data as CreateChallengeResponse
}

/** CoreV2: PATCH /challenges-admin/v1/challenges/{challengeId} */
export const updateChallengeV2 = async (
  challengeId: string,
  body: UpdateChallengePayload,
): Promise<UpdateChallengeResponse> => {
  const response = await apiPATCH({ url: `/api/2/challenges/${challengeId}`, data: body })
  return response.data as UpdateChallengeResponse
}

/** CoreV2: POST /challenges-admin/v1/challenges/{challengeId}/state */
export const transitionChallengeState = async (
  challengeId: string,
  body: TransitionChallengeStatePayload,
): Promise<TransitionChallengeStateResponse> => {
  const response = await apiPOST({ url: `/api/2/challenges/${challengeId}/state`, data: body })
  return response.data as TransitionChallengeStateResponse
}

/** CoreV2: GET /challenges-admin/v1/challenges/{challengeId}/leaderboard */
export const getAdminLeaderboard = async (challengeId: string): Promise<GetLeaderboardResponse> => {
  const response = await apiGET({ url: `/api/2/challenges/${challengeId}/leaderboard` })
  return response.data as GetLeaderboardResponse
}

/** CoreV2: POST /challenges-media/v1/challenges/{challengeId}/image/{challengeImageType}/request-upload */
export const requestChallengeImageUpload = async (
  challengeId: string,
  challengeImageType: ChallengeImageType,
): Promise<RequestImageUploadResponse> => {
  const response = await apiPOST({
    url: `/api/2/challenges/${challengeId}/image/${challengeImageType}/request-upload`,
  })
  return response.data as RequestImageUploadResponse
}

/** CoreV2: POST /challenges-media/v1/challenges/{challengeId}/image/{challengeImageType}/confirm-upload */
export const confirmChallengeImageUpload = (
  challengeId: string,
  challengeImageType: ChallengeImageType,
  params?: { waitForCompletion?: boolean },
): Promise<ConfirmImageUploadResponse> =>
  apiPOST({
    url: `/api/2/challenges/${challengeId}/image/${challengeImageType}/confirm-upload`,
    params,
  }).then((response) => response.data as ConfirmImageUploadResponse)

/** CoreV2: DELETE /challenges-media/v1/challenges/{challengeId}/image/{challengeImageType} */
export const deleteChallengeImage = async (
  challengeId: string,
  challengeImageType: ChallengeImageType,
): Promise<DeleteImageResponse> => {
  const response = await apiDELETE({ url: `/api/2/challenges/${challengeId}/image/${challengeImageType}` })
  return response.data as DeleteImageResponse
}

/** CoreV2: POST /challenges-admin/v1/challenges/{challengeId}/recalculate */
export const recalculateChallenge = async (
  challengeId: string,
  participantIds?: string[],
): Promise<RecalculateChallengeResponse> => {
  const response = await apiPOST({
    url: `/api/2/challenges/${challengeId}/recalculate`,
    params: participantIds?.length ? { participantIds } : undefined,
  })
  return response.data as RecalculateChallengeResponse
}

/** CoreV2:
 * POST /challenges-admin/v1/challenges/{challengeId}/participants/{participantId}/rounds/{roundId}/override-eligibility
 *  */
export const overrideRoundEligibility = async (
  challengeId: string,
  participantId: string,
  roundId: string,
  body?: OverrideRoundEligibilityPayload,
): Promise<OverrideRoundEligibilityResponse> =>
  apiPOST({
    url: `/api/2/challenges/${challengeId}/participants/${participantId}/rounds/${roundId}/override-eligibility`,
    data: body,
  }).then((response) => response.data as OverrideRoundEligibilityResponse)
